// A sessão é do Firebase Auth, a mesma do painel legado: quem entrou pelo
// /login continua logado aqui (mesma origem, mesma persistência do SDK). O
// token vai no Authorization de toda chamada; o backend o verifica.
//
// A configuração é pública por natureza (a chave web do Firebase identifica
// o projeto, não autoriza nada) — é a mesma que está nos HTMLs do legado.
import { initializeApp } from 'firebase/app';
import { applyActionCode, confirmPasswordReset, createUserWithEmailAndPassword, getAdditionalUserInfo, getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, signOut, updateProfile, verifyPasswordResetCode, type User } from 'firebase/auth';

/**
 * De onde a janela do login com o Google abre (2026-09-29).
 *
 * Com o `firebaseapp.com`, o Google mostrava "continuar para
 * orren-agents.firebaseapp.com" — o nome do projeto, e não a marca. Na
 * produção a janela abre no PRÓPRIO domínio: o Hosting serve o
 * `/__/auth/handler` em todo domínio de um site do projeto, e esse endereço está
 * cadastrado como retorno do cliente OAuth. Nos outros lugares (staging,
 * prévias, localhost) continua o `firebaseapp.com`, que funciona em qualquer
 * um sem cadastrar retorno por ambiente.
 */
export function dominioDoLogin(host: string): string {
  return host === 'borainbox.com.br' || host === 'www.borainbox.com.br' ? 'borainbox.com.br' : 'orren-agents.firebaseapp.com';
}

const app = initializeApp({
  apiKey: 'AIzaSyAxJ10Y6Zdjlkox3j93cic4V2D3aa54StU',
  authDomain: dominioDoLogin(location.hostname),
  projectId: 'orren-agents',
  storageBucket: 'orren-agents.firebasestorage.app',
  messagingSenderId: '86899364251',
  appId: '1:86899364251:web:6d1742a4ac8a9b5fae513e',
});

export const auth = getAuth(app);

/** Resolve uma vez com o usuário atual (ou null), depois de o SDK restaurar a sessão. */
export function usuarioAtual(): Promise<User | null> {
  return new Promise((ok) => {
    const parar = onAuthStateChanged(auth, (u) => { parar(); ok(u); });
  });
}

/** O token pra API. O SDK renova sozinho antes de vencer; sem usuário, o que o /login guardou. */
export async function obterToken(): Promise<string> {
  const u = auth.currentUser;
  if (!u) return sessionStorage.getItem('auth_token') || '';
  const token = await u.getIdToken();
  sessionStorage.setItem('auth_token', token);
  return token;
}

/** O que o Firebase responde, em português. O que não está aqui vira a frase genérica. */
export const MENSAGENS_DO_AUTH: Record<string, string> = {
  'auth/invalid-credential': 'E-mail ou senha inválidos',
  'auth/user-not-found': 'Usuário não encontrado',
  'auth/wrong-password': 'Senha incorreta',
  'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde.',
  'auth/invalid-email': 'E-mail inválido',
  'auth/email-already-in-use': 'Este e-mail já está em uso',
  'auth/weak-password': 'Senha muito fraca',
  // Entrar com o Google (2026-09-27).
  'auth/popup-blocked': 'O navegador bloqueou a janela do Google. Libere pop-ups para este site e tente de novo.',
  'auth/popup-closed-by-user': 'A janela do Google foi fechada antes de terminar.',
  'auth/cancelled-popup-request': 'A janela do Google foi fechada antes de terminar.',
  'auth/unauthorized-domain': 'Este endereço ainda não está liberado para entrar com o Google.',
  'auth/operation-not-allowed': 'Entrar com o Google ainda não está ativado.',
  'auth/account-exists-with-different-credential': 'Já existe uma conta com este e-mail. Entre com e-mail e senha.',
  // Os links dos e-mails de conta (2026-09-27).
  'auth/expired-action-code': 'Este link venceu. Peça um novo.',
  'auth/invalid-action-code': 'Este link não vale mais: ele já foi usado ou um mais novo foi enviado. Peça um novo.',
  'auth/user-disabled': 'Esta conta está desativada.',
};

/*
 * Os links dos e-mails de conta chegam no NOSSO domínio (`/app/senha-nova`,
 * `/app/confirmar-email`) só com o código do Firebase; é ele que valida e
 * expira. Antes o link ia para a página genérica do Firebase, em inglês e em
 * outro domínio — cara de golpe num e-mail que pede para trocar senha.
 */

/** O código de troca de senha ainda vale? Devolve o e-mail da conta, para a tela dizer de quem é. */
export const conferirCodigoDeSenha = (codigo: string) => verifyPasswordResetCode(auth, codigo);
export const trocarSenhaComCodigo = (codigo: string, senha: string) => confirmPasswordReset(auth, codigo, senha);
export const confirmarEmailComCodigo = (codigo: string) => applyActionCode(auth, codigo);
export const mensagemDoAuth = (e: unknown, padrao: string) => { const c = (e as { code?: string })?.code; return (c && MENSAGENS_DO_AUTH[c]) || padrao; };

export async function entrarComEmail(email: string, senha: string): Promise<User> {
  const { user } = await signInWithEmailAndPassword(auth, email, senha);
  sessionStorage.setItem('auth_token', await user.getIdToken());
  return user;
}

export async function criarConta(nome: string, email: string, senha: string): Promise<User> {
  const { user } = await createUserWithEmailAndPassword(auth, email, senha);
  await updateProfile(user, { displayName: nome });
  sessionStorage.setItem('auth_token', await user.getIdToken());
  return user;
}

/**
 * Entrar (ou criar a conta) com o Google, numa janela.
 *
 * Janela, e não redirecionamento: fora da produção o `authDomain` é o
 * firebaseapp.com e o painel mora em outro domínio; o redirecionamento depende
 * de armazenamento de terceiros que os navegadores novos bloqueiam, a janela
 * conversa por postMessage e não depende disso.
 *
 * `novo` diz se a conta nasceu agora: quem entra pelo Google pela primeira vez
 * ainda não deu o WhatsApp, que o cadastro por e-mail pede — o painel pede em
 * seguida. Mesmo e-mail de uma conta com senha: o Firebase entra na MESMA conta
 * (um e-mail, uma conta), então a loja é a mesma.
 */
export async function entrarComGoogle(): Promise<{ user: User; novo: boolean }> {
  const provedor = new GoogleAuthProvider();
  provedor.setCustomParameters({ prompt: 'select_account' });
  const r = await signInWithPopup(auth, provedor);
  sessionStorage.setItem('auth_token', await r.user.getIdToken());
  return { user: r.user, novo: !!getAdditionalUserInfo(r)?.isNewUser };
}

export async function sair(): Promise<void> {
  const token = sessionStorage.getItem('auth_token');
  sessionStorage.removeItem('auth_token');
  // Avisa o backend pra esquecer o token no cache dele; se não der, o token vence sozinho.
  if (token) await fetch('/auth/logout', { method: 'POST', headers: { Authorization: 'Bearer ' + token } }).catch(() => {});
  await signOut(auth);
}

export { onAuthStateChanged };
export type { User };
